import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Check,
  ChevronLeft,
  ChevronRight,
  ListOrdered,
  Loader2,
  LogIn,
  Presentation,
  RotateCcw,
  X,
} from 'lucide-react'
import { getAccessToken, getAuthConfig, refreshAccessToken } from '../auth/authStore'
import {
  buildDemoSteps,
  DEMO_APPS,
  type DemoApp,
  type DemoStep,
  type DemoStory,
} from './demoSteps'

/**
 * The presenter's guide for a live demo across the athlete and brand apps: a card in the top
 * right with the step, what to say and do, and Previous / Next. A step can live in the other
 * app and as another account; the card opens that app and signs in as the step's account, so
 * the demo runs without stopping to log in and out.
 *
 * Start it with ?demo=1 on any page (or ?demo=<step>). It remembers the step in this tab until
 * you exit. Never shown on production; the server's /api/demo endpoints refuse there too.
 */

const STORE_KEY = 'athios_demo_step'
const HIDE_KEY = 'athios_demo_hidden'

const read = (key: string) => {
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}
const write = (key: string, value: string | null) => {
  try {
    if (value == null) sessionStorage.removeItem(key)
    else sessionStorage.setItem(key, value)
  } catch {
    // Private mode: the step lives in the URL only
  }
}

/** The demo never runs on the live product */
const demoAllowed = () =>
  typeof window !== 'undefined' &&
  !/(^|\.)athios\.ai$/.test(window.location.hostname) &&
  (import.meta as any).env?.VITE_DEMO_FLOW !== 'false'

/**
 * Where the other app lives, worked out from where this one is: local ports (8001 athlete,
 * 8002 marketer), portless names (athlete-flow.localhost), or path prefixes (/athlete, /marketer).
 * VITE_DEMO_ATHLETE_URL / VITE_DEMO_MARKETER_URL override it.
 */
function demoAppBase(app: DemoApp, current: DemoApp): string {
  const env = (import.meta as any).env ?? {}
  const override = app === 'athlete' ? env.VITE_DEMO_ATHLETE_URL : env.VITE_DEMO_MARKETER_URL
  if (override) return String(override).replace(/\/$/, '')
  const { protocol, hostname, port, pathname } = window.location
  const base = ((import.meta as any).env?.BASE_URL ?? '/').replace(/\/$/, '')
  if (app === current) return `${window.location.origin}${base}`
  const ports: Record<DemoApp, string> = { athlete: '8001', marketer: '8002' }
  if (port === ports[current]) return `${protocol}//${hostname}:${ports[app]}`
  const names: Record<DemoApp, string> = { athlete: 'athlete-flow', marketer: 'marketer-flow' }
  if (hostname.startsWith(`${names[current]}.`))
    return `${protocol}//${hostname.replace(names[current], names[app])}${port ? `:${port}` : ''}`
  const prefixes: Record<DemoApp, string> = { athlete: '/athlete', marketer: '/marketer' }
  if (pathname.startsWith(prefixes[current])) return `${window.location.origin}${prefixes[app]}`
  return `${window.location.origin}${base}`
}

async function api(path: string, init: RequestInit = {}) {
  const { apiBaseUrl, appId } = getAuthConfig()
  const token = getAccessToken()
  const res = await fetch(`${apiBaseUrl}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(appId ? { 'X-App-Id': appId } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  })
  const body = await res.json().catch(() => null)
  if (!res.ok) throw new Error(body?.error ?? `Request failed (${res.status})`)
  return body
}

/** Who is signed in to this app right now (null when nobody) */
async function currentEmail(): Promise<string | null> {
  if (!getAccessToken()) await refreshAccessToken().catch(() => null)
  if (!getAccessToken()) return null
  try {
    const me = await api('/users/me')
    return ((me?.user ?? me)?.email ?? null)?.toLowerCase() ?? null
  } catch {
    return null
  }
}

export function DemoFlow({ app }: { app: DemoApp }) {
  const [step, setStep] = useState<number | null>(() => {
    const fromUrl = Number(new URLSearchParams(window.location.search).get('demo'))
    if (fromUrl > 0) return fromUrl
    const stored = Number(read(STORE_KEY))
    return stored > 0 ? stored : null
  })
  const [story, setStory] = useState<DemoStory | null>(null)
  const [hidden, setHidden] = useState(() => read(HIDE_KEY) === '1')
  const [showList, setShowList] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [confirmReset, setConfirmReset] = useState(false)
  // On a phone the script starts folded so the card doesn't cover the screen being shown
  const [showScript, setShowScript] = useState(() => window.innerWidth >= 640)

  const enabled = demoAllowed() && step !== null
  useEffect(() => {
    if (!enabled) return
    api('/demo/story')
      .then((s) => {
        setStory(s)
        if (!s?.ready) setError(s?.error ?? 'The demo isn’t set up on this server.')
      })
      .catch((e) => setError(e.message))
  }, [enabled])

  const steps = useMemo(() => (story ? buildDemoSteps(story) : []), [story])
  const current = steps.find((s) => s.n === step) ?? null

  useEffect(() => {
    if (step !== null) write(STORE_KEY, String(step))
  }, [step])

  /** Opens a step: in this app or the other one, signed in as the step's account */
  const go = useCallback(
    async (target: DemoStep, opts: { arriving?: boolean } = {}) => {
      setError(null)
      setShowList(false)
      setStep(target.n)
      const dest = `${demoAppBase(target.app, app)}${target.path}${target.path.includes('?') ? '&' : '?'}demo=${target.n}`
      if (target.app !== app) {
        window.location.href = dest
        return
      }
      setBusy(`Opening step ${target.n}…`)
      try {
        const who = await currentEmail()
        if (who !== target.account.email.toLowerCase()) {
          setBusy(`Signing in as ${target.account.name}…`)
          const res = await api('/users/login', {
            method: 'POST',
            body: JSON.stringify({
              email: target.account.email,
              password: target.account.password,
              user_type: target.account.userType,
            }),
          })
          if (!res?.token) throw new Error('Sign-in failed')
        } else if (opts.arriving) {
          const here = window.location.pathname.replace(/\/$/, '')
          const there = new URL(dest).pathname.replace(/\/$/, '')
          if (here === there) {
            setBusy(null)
            return
          }
        }
        window.location.replace(dest)
      } catch (e: any) {
        setBusy(null)
        setError(`${e.message}. Is the demo set up? Try Reset demo.`)
      }
    },
    [app],
  )

  // Arriving with ?demo=N: make sure this is the right account and page
  const [arrived, setArrived] = useState(false)
  useEffect(() => {
    if (!current || arrived) return
    setArrived(true)
    // Arriving from a step link, or bounced to sign-in on the way (which drops ?demo)
    const linked = new URLSearchParams(window.location.search).get('demo')
    const signedOut = /\/sign(in|up)/.test(window.location.pathname)
    if ((linked || signedOut) && current.app === app) go(current, { arriving: true })
  }, [current, arrived, app, go])

  if (!enabled) return null

  const exit = () => {
    write(STORE_KEY, null)
    write(HIDE_KEY, null)
    const url = new URL(window.location.href)
    url.searchParams.delete('demo')
    window.history.replaceState(null, '', url.toString())
    setStep(null)
  }
  const reset = async () => {
    setConfirmReset(false)
    setBusy('Resetting the demo…')
    try {
      const fresh = await api('/demo/reset', { method: 'POST' })
      setStory(fresh)
      const first = buildDemoSteps(fresh)[0]
      if (first) await go(first)
      else setBusy(null)
    } catch (e: any) {
      setBusy(null)
      setError(e.message)
    }
  }

  if (hidden) {
    return (
      <button
        type="button"
        onClick={() => {
          setHidden(false)
          write(HIDE_KEY, null)
        }}
        className="fixed right-3 top-3 z-[100] flex items-center gap-1.5 rounded-full bg-[#C0FD72] px-3 py-2 text-xs font-bold text-[#0F172A] shadow-xl"
      >
        <Presentation className="h-3.5 w-3.5" />
        Demo · {step}/{steps.length || '…'}
      </button>
    )
  }

  const idx = current ? steps.indexOf(current) : -1
  const prev = idx > 0 ? steps[idx - 1] : null
  const next = idx >= 0 ? (steps[idx + 1] ?? null) : null
  const elsewhere = current && current.app !== app

  return (
    <div className="fixed right-3 top-3 z-[100] w-[min(360px,calc(100vw-24px))] overflow-hidden rounded-2xl border border-[#C0FD72]/50 bg-[#0F172A] text-[#FFFFFF] shadow-2xl">
      <div className="flex items-center gap-2 border-b border-[#FFFFFF]/10 px-3 py-2.5">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#C0FD72]/20">
          <Presentation className="h-3.5 w-3.5 text-[#C0FD72]" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#C0FD72]">
            Demo flow{current ? ` · ${current.act}` : ''}
          </p>
          <p className="truncate text-xs text-[#CBD5E1]">
            {current
              ? `Step ${current.n} of ${steps.length} · ${DEMO_APPS[current.app]} · ${current.account.name}`
              : 'Loading…'}
          </p>
        </div>
        <button
          type="button"
          aria-label="All steps"
          onClick={() => setShowList((v) => !v)}
          className="rounded-md p-1.5 text-[#94A3B8] hover:bg-[#FFFFFF]/10 hover:text-[#FFFFFF]"
        >
          <ListOrdered className="h-4 w-4" />
        </button>
        <button
          type="button"
          aria-label="Hide the demo card"
          onClick={() => {
            setHidden(true)
            write(HIDE_KEY, '1')
          }}
          className="rounded-md p-1.5 text-[#94A3B8] hover:bg-[#FFFFFF]/10 hover:text-[#FFFFFF]"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Progress: one segment per step, grouped by act */}
      <div className="flex gap-0.5 px-3 pt-2.5">
        {steps.map((s) => (
          <button
            key={s.n}
            type="button"
            onClick={() => go(s)}
            title={`${s.n}. ${s.title}`}
            aria-label={`Step ${s.n}: ${s.title}`}
            className={`h-1.5 flex-1 rounded-full ${
              s.n === step
                ? 'bg-[#C0FD72]'
                : s.n < (step ?? 0)
                  ? 'bg-[#C0FD72]/40'
                  : s.app === 'athlete'
                    ? 'bg-[#FFFFFF]/15'
                    : 'bg-[#93C5FD]/25'
            }`}
          />
        ))}
      </div>

      {showList ? (
        <div className="max-h-[50vh] overflow-y-auto px-2 py-2">
          {steps.map((s, i) => (
            <div key={s.n}>
              {i === 0 || steps[i - 1].act !== s.act ? (
                <p className="px-2 pb-1 pt-2 text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">
                  {s.act}
                </p>
              ) : null}
              <button
                type="button"
                onClick={() => go(s)}
                className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs ${
                  s.n === step
                    ? 'bg-[#C0FD72]/15 text-[#C0FD72]'
                    : 'text-[#E2E8F0] hover:bg-[#FFFFFF]/5'
                }`}
              >
                <span className="w-5 shrink-0 text-right tabular-nums text-[#94A3B8]">{s.n}</span>
                <span className="min-w-0 flex-1">{s.title}</span>
                <span className="shrink-0 text-[10px] text-[#94A3B8]">
                  {s.app === 'athlete' ? 'Athlete' : 'Brand'}
                </span>
              </button>
            </div>
          ))}
        </div>
      ) : current ? (
        <div className="space-y-2 px-3.5 py-3">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-bold leading-snug">{current.title}</p>
            <button
              type="button"
              onClick={() => setShowScript((v) => !v)}
              className="shrink-0 text-[11px] font-semibold text-[#94A3B8] underline hover:text-[#FFFFFF]"
            >
              {showScript ? 'Hide script' : 'Script'}
            </button>
          </div>
          {showScript ? (
            <>
              <p className="text-xs leading-relaxed text-[#CBD5E1]">{current.say}</p>
              <p className="rounded-lg border border-[#C0FD72]/25 bg-[#C0FD72]/[0.07] px-2.5 py-1.5 text-[11px] font-medium leading-relaxed text-[#C0FD72]">
                → {current.do}
              </p>
            </>
          ) : null}
          {elsewhere ? (
            <button
              type="button"
              onClick={() => go(current)}
              className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#C0FD72] px-3 py-2 text-xs font-bold text-[#0F172A]"
            >
              <LogIn className="h-3.5 w-3.5" /> Open in the {DEMO_APPS[current.app]} as{' '}
              {current.account.name}
            </button>
          ) : null}
        </div>
      ) : (
        <p className="px-3.5 py-3 text-xs text-[#CBD5E1]">{error ? '' : 'Loading the demo…'}</p>
      )}

      {busy ? (
        <p className="flex items-center gap-2 px-3.5 pb-2 text-xs text-[#C0FD72]">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> {busy}
        </p>
      ) : null}
      {error ? <p className="px-3.5 pb-2 text-xs text-[#FCA5A5]">{error}</p> : null}

      {confirmReset ? (
        <div className="space-y-2 border-t border-[#FFFFFF]/10 px-3.5 py-3">
          <p className="text-xs text-[#E2E8F0]">
            Put the demo accounts and deals back to the start? Anything done during the demo is
            undone; other data is left alone.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={reset}
              className="flex-1 rounded-lg bg-[#C0FD72] py-1.5 text-xs font-bold text-[#0F172A]"
            >
              Reset and start over
            </button>
            <button
              type="button"
              onClick={() => setConfirmReset(false)}
              className="flex-1 rounded-lg border border-[#FFFFFF]/20 py-1.5 text-xs font-semibold"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      <div className="flex items-stretch border-t border-[#FFFFFF]/10 text-xs">
        <button
          type="button"
          onClick={() => prev && go(prev)}
          disabled={!prev || !!busy}
          className="flex flex-1 items-center justify-center gap-1 py-2.5 text-[#CBD5E1] hover:bg-[#FFFFFF]/5 disabled:opacity-30"
        >
          <ChevronLeft className="h-3.5 w-3.5" /> Back
        </button>
        <span className="w-px bg-[#FFFFFF]/10" />
        <button
          type="button"
          onClick={() => next && go(next)}
          disabled={!next || !!busy}
          className="flex flex-[1.6] items-center justify-center gap-1 py-2.5 font-bold text-[#C0FD72] hover:bg-[#C0FD72]/10 disabled:opacity-30"
        >
          {next ? (
            <>
              Next{next.app !== current?.app ? ` (${DEMO_APPS[next.app]})` : ''}
              <ChevronRight className="h-3.5 w-3.5" />
            </>
          ) : (
            <>
              <Check className="h-3.5 w-3.5" /> The end
            </>
          )}
        </button>
      </div>
      <div className="flex items-center justify-between border-t border-[#FFFFFF]/10 px-3 py-1.5 text-[11px] text-[#94A3B8]">
        <button
          type="button"
          onClick={() => setConfirmReset(true)}
          className="inline-flex items-center gap-1 hover:text-[#FFFFFF]"
        >
          <RotateCcw className="h-3 w-3" /> Reset demo
        </button>
        <button type="button" onClick={exit} className="hover:text-[#FFFFFF]">
          Exit demo
        </button>
      </div>
    </div>
  )
}

export default DemoFlow
